import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { View } from "@data-root/react";
import {
  EnumOption,
  RowWithEmbeddedMetadata,
  SchemaItem,
} from "@data-root/types";
import { Dialog } from "../portals/Dialog";
import { Select } from "../Select";
import { CaretDownOutlined } from "@ant-design/icons";
import { Button, Skeleton, Input as _Input } from "antd";
import { SchemaItemProps, useField } from "@data-root/react";
import { Typography } from "antd";
import { debounce } from "lodash";
import { Remarks } from "../remarks";

const { Title } = Typography;

export interface InputWithOptionsProps<T extends SchemaItem = SchemaItem>
  extends SchemaItemProps<T> {
  options: EnumOption[];
  multiple?: boolean;
}

export interface InputProps<T extends SchemaItem = SchemaItem, U = any>
  extends SchemaItemProps<T> {
  multiline?: boolean;
  toValue?: (value: string) => U;
  _toString?: (value: U) => string;
}

export const InputWithOptions: FC<InputWithOptionsProps> = ({
  item,
  options,
  multiple = false,
}) => {
  const {
    loaded,
    displayName,
    placeholder,
    editable,
    show,
    value: _value,
    displayValue: _displayValue,
    setValue: _setValue,
    remarks,
    subscribe,
  } = useField(item);

  const [visible, setVisible] = useState(false);

  const [value, setValue] = useState(_value as string | string[]);

  const displayValue = useMemo(
    () => options.find((option) => option.id === value)?.name ?? "",
    [options, value]
  );

  useEffect(() => {
    const callback = (
      next: RowWithEmbeddedMetadata,
      options?: {
        init?: boolean;
        evaluation?: boolean;
      }
    ) => {
      if (options?.init) {
        setValue(next[item.id] as any);
        return;
      }
      if (!options?.evaluation) return;

      setValue(next[item.id] as any);
    };

    const unsubscribe = subscribe(callback);

    return () => {
      unsubscribe();
    };
  }, [item.id, subscribe]);

  const handleOnOpenDialog = useCallback(() => {
    setVisible(true);
  }, [setVisible]);

  const handleSetValue = useCallback(
    (value: string | string[], displayValue: string) => {
      if (!multiple) setVisible(false);
      setValue(value);
      _setValue(value, displayValue);
    },
    [_setValue, multiple]
  );

  if (!show) return null;

  return (
    <View style={{ width: "100%" }}>
      <Title level={5}>{displayName}</Title>

      {!loaded ? (
        <Skeleton.Input block active />
      ) : (
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 10,
          }}
        >
          <_Input
            readOnly
            maxLength={10}
            placeholder={placeholder}
            value={displayValue}
          />

          {editable && (
            <Button
              type="primary"
              icon={<CaretDownOutlined />}
              onClick={handleOnOpenDialog}
            />
          )}
        </View>
      )}

      <Dialog title={displayName} visible={visible} setVisible={setVisible}>
        <Select
          multiple={multiple}
          value={value}
          setValue={handleSetValue}
          items={options}
        />
      </Dialog>

      <Remarks texts={remarks} />
    </View>
  );
};

// TODO: Figure out what to do with the display value
export const Input: FC<InputProps> = ({
  item,
  toValue,
  _toString,
  multiline = false,
}) => {
  console.group("Input", item.id);

  console.log("item", item);

  const {
    loaded,
    displayName,
    placeholder,
    editable,
    show,
    value,
    displayValue,
    setValue,
    remarks,
  } = useField(item);

  console.log("value", value);
  console.log("displayValue", displayValue);

  const formattedValue = useMemo(() => {
    if (_toString) return _toString(value);
    return value as string;
  }, [value, _toString]);

  const setFormattedValue = useCallback(
    (formattedValue: string) => {
      if (formattedValue === "") setValue(null, "");
      else if (toValue) setValue(toValue(formattedValue), formattedValue);
      else setValue(formattedValue, formattedValue);
    },
    [setValue, toValue]
  );

  if (!show) {
    console.warn("show is false");
    console.groupEnd();

    return null;
  }

  console.groupEnd();

  return (
    <View style={{ width: "100%" }}>
      <Title level={5}>{displayName}</Title>

      {!loaded ? (
        <Skeleton.Input block active />
      ) : multiline ? (
        <_Input.TextArea
          readOnly={!editable}
          placeholder={placeholder}
          value={formattedValue}
          onChange={(event) => setFormattedValue(event.target.value)}
          style={{
            resize: "vertical",
            minHeight: 500,
          }}
        />
      ) : (
        <_Input
          readOnly={!editable}
          placeholder={placeholder}
          value={formattedValue}
          onChange={(event) => setFormattedValue(event.target.value)}
        />
      )}

      <Remarks texts={remarks} />
    </View>
  );
};
